import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Row, Col, Form, Accordion, Card, Button, Alert, Spinner } from "react-bootstrap";
import { FaChalkboardTeacher } from "react-icons/fa";
import SchoolRollNumberInput from "./SchoolRollNumberInput";
import DeleteAnswerModal from "./DeleteAnswerModal";
import {
  getClasses,
  getClassesBySchoolId,
  getStudentsByClassId,
  getIndividualStudentResults,
  searchTeacherStudents,
  whoami,
  getSchools
} from "../services/APIClient";
import { isTeacher } from "../utils/auth";
import secondsToTime from "../services/secondsToTime";
import "./ResetTasksFlow.css";

export const RESET_TASK_PROGRESS_STEPS = [
  "Select class/student",
  "Select assessment/task",
  "Choose retake reason",
  "Confirm reset",
  "Success/next steps"
];

const GRADE_LEVELS = [
  { value: "grade_level1", label: "Junior Infants" },
  { value: "grade_level2", label: "Senior Infants" },
  { value: "grade_level3", label: "1st Class" },
  { value: "grade_level4", label: "2nd Class" },
  { value: "grade_level5", label: "3rd Class" },
  { value: "grade_level6", label: "4th Class" },
  { value: "grade_level7", label: "5th Class" },
  { value: "grade_level8", label: "6th Class" }
];

const GRADE_FILTER_OPTIONS = [
  { value: "all", label: "All Grades" },
  ...GRADE_LEVELS.slice(0, 3)
];

const STUDENT_SEARCH_MIN_LENGTH = 2;
const STUDENT_SEARCH_LIMIT = 25;
const STUDENT_SEARCH_DEBOUNCE_MS = 300;

const normaliseText = (value) =>
  String(value || "")
    .trim()
    .replace(/\s+/g, " ");

const normaliseComparableText = (value) => normaliseText(value).toLowerCase();

const normaliseResetTaskPrefill = (prefill) => ({
  studentName: normaliseText(prefill?.studentName || prefill?.student_name),
  className: normaliseText(prefill?.className || prefill?.class_name),
  assessmentName: normaliseText(prefill?.assessmentName || prefill?.assessment_name),
  taskName: normaliseText(prefill?.taskName || prefill?.task_name),
  reason: normaliseText(prefill?.reason)
});

const getSingleMatchingStudent = (students, prefill) => {
  if (!prefill.studentName) {
    return null;
  }

  const matchingStudents = students.filter((student) => {
    const matchesStudentName =
      normaliseComparableText(student.name) === normaliseComparableText(prefill.studentName);
    const matchesClassName =
      !prefill.className ||
      normaliseComparableText(student.className) === normaliseComparableText(prefill.className) ||
      normaliseComparableText(student.classDisplayName).includes(
        normaliseComparableText(prefill.className)
      );

    return matchesStudentName && matchesClassName;
  });

  return matchingStudents.length === 1 ? matchingStudents[0] : null;
};

const getSingleMatchingClass = (classes, className) => {
  if (!className) {
    return null;
  }

  const matchingClasses = classes.filter(
    (classItem) => normaliseComparableText(classItem.name) === normaliseComparableText(className)
  );

  return matchingClasses.length === 1 ? matchingClasses[0] : null;
};

const getPrefilledTaskTarget = (studentResults, prefill) => {
  if (!prefill.taskName || studentResults.length < 1) {
    return null;
  }

  const matchingAssessments = studentResults.filter(
    (assessment) =>
      !prefill.assessmentName ||
      normaliseComparableText(assessment.name) === normaliseComparableText(prefill.assessmentName)
  );
  const matchingTasks = matchingAssessments.flatMap((assessment) =>
    assessment.tasks
      .filter(
        (task) => normaliseComparableText(task.name) === normaliseComparableText(prefill.taskName)
      )
      .map((task) => ({ assessment, task }))
  );

  return matchingTasks.length === 1 ? matchingTasks[0] : null;
};

const getGradeBooleanFromClass = (classItem) => {
  const gradeLevel = GRADE_LEVELS.find((grade) => classItem?.[grade.value]);
  return gradeLevel?.value || classItem?.gradeBoolean || "unknown";
};

const getGradeLabelFromClass = (classItem) => {
  const gradeBoolean = getGradeBooleanFromClass(classItem);
  const gradeLevel = GRADE_LEVELS.find((grade) => grade.value === gradeBoolean);
  return gradeLevel?.label || classItem?.grade || "";
};

const getAcademicYearFromClass = (classItem) =>
  classItem?.academicYear ||
  classItem?.academic_year ||
  classItem?.academicyear ||
  classItem?.academicYearLabel ||
  classItem?.yearLabel ||
  classItem?.schoolYear ||
  null;

const formatClassDisplayName = (classItem) => {
  const name = classItem?.name || "Unnamed Class";
  const academicYear = getAcademicYearFromClass(classItem);
  const gradeLabel = getGradeLabelFromClass(classItem);
  const prefix = academicYear ? `[${academicYear}] ` : "";
  const suffix = gradeLabel ? ` - ${gradeLabel}` : "";

  return `${prefix}${name}${suffix}`;
};

const normaliseClass = (classItem) => {
  const academicYear = getAcademicYearFromClass(classItem);
  const gradeBoolean = getGradeBooleanFromClass(classItem);
  const grade = getGradeLabelFromClass(classItem);

  return {
    ...classItem,
    name: classItem?.name || "Unnamed Class",
    academicYear,
    gradeBoolean,
    grade,
    displayName: formatClassDisplayName({
      ...classItem,
      academicYear,
      gradeBoolean,
      grade
    })
  };
};

const normaliseStudentSearchResult = (student) => ({
  ...student,
  id: student?.id,
  name: student?.name || "Unnamed Student",
  classId: student?.classId,
  className: student?.className || "Class unavailable",
  classDisplayName: student?.classDisplayName || student?.className || "Class unavailable",
  academicYear: student?.academicYear || null,
  grade: student?.grade || "",
  student: {
    id: student?.id,
    name: student?.name || "Unnamed Student"
  }
});

function ResetTasksFlow({
  teacherMode = isTeacher(),
  modalMode = false,
  initialSearchQuery = "",
  onSearchQueryChange,
  onProgressStepChange,
  onUserLoaded,
  prefill = null,
  onWorkflowComplete = null
}) {
  const [schoolid, setSchoolid] = useState("");
  const [schoolName, setSchoolName] = useState("Loading school information...");
  const [rollNumber, setRollNumber] = useState("");
  const [isLoadingSchool, setIsLoadingSchool] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedStudentName, setSelectedStudentName] = useState("");
  const [studentResults, setStudentResults] = useState([]);
  const [accessibleStudents, setAccessibleStudents] = useState([]);
  const [studentSearchTerm, setStudentSearchTerm] = useState("");
  const [isLoadingStudentDirectory, setIsLoadingStudentDirectory] = useState(false);
  const [studentDirectoryError, setStudentDirectoryError] = useState(null);
  const [hasLoadedStudentDirectory, setHasLoadedStudentDirectory] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTaskInfo, setSelectedTaskInfo] = useState(null);
  const [teacherid, setTeacherid] = useState(null);
  const [searchQuery, setSearchQuery] = useState(initialSearchQuery);
  const [classSearchTerm, setClassSearchTerm] = useState("");
  const [classGradeFilter, setClassGradeFilter] = useState("all");
  const [isClassFallbackOpen, setIsClassFallbackOpen] = useState(false);
  const [hasAppliedClassPrefill, setHasAppliedClassPrefill] = useState(false);
  const [hasAppliedStudentPrefill, setHasAppliedStudentPrefill] = useState(false);
  const [hasAppliedTaskPrefill, setHasAppliedTaskPrefill] = useState(false);
  const isTeacherStudentFirstFlow = teacherMode && modalMode;
  const prefillContext = useMemo(() => normaliseResetTaskPrefill(prefill), [prefill]);
  const prefillKey = useMemo(() => JSON.stringify(prefillContext), [prefillContext]);

  const isSchoolFound =
    schoolid &&
    schoolid !== "" &&
    schoolName !== "Loading school information..." &&
    schoolName !== "Please enter school roll number and press 'Find School'";

  useEffect(() => {
    if (!modalMode) {
      setSearchQuery(initialSearchQuery);
    }
  }, [initialSearchQuery, modalMode]);

  useEffect(() => {
    onProgressStepChange?.(0);
  }, [onProgressStepChange]);

  useEffect(() => {
    setStudentSearchTerm(prefillContext.studentName);
    setClassSearchTerm(prefillContext.className);
    setSearchQuery(prefillContext.assessmentName || prefillContext.taskName || initialSearchQuery);
    setIsClassFallbackOpen(Boolean(prefillContext.className && !prefillContext.studentName));
    setHasAppliedClassPrefill(false);
    setHasAppliedStudentPrefill(false);
    setHasAppliedTaskPrefill(false);
  }, [initialSearchQuery, prefillKey]);

  useEffect(() => {
    const fetchUserAndSchoolInfo = async () => {
      try {
        const response = await whoami();
        const stakeholder = response.data.stakeholder;
        setTeacherid(stakeholder.id);
        onUserLoaded?.({ name: stakeholder.name, teacherid: stakeholder.id });

        if (teacherMode) {
          const teacherSchoolId = stakeholder.schoolid || stakeholder.schoolId;

          if (!teacherSchoolId) {
            console.error("Teacher has no school ID.");
            setSchoolName("Error: No school assigned");
            setLoadError("No school assigned to your account");
            setIsLoadingSchool(false);
            return;
          }

          setSchoolid(teacherSchoolId);

          try {
            const response = await getSchools();
            const schools = response.data || response;
            const teacherSchool = Array.isArray(schools)
              ? schools.find((school) => school.id === teacherSchoolId)
              : null;

            if (teacherSchool) {
              setSchoolName(teacherSchool.name);
              setRollNumber(teacherSchool.rollnumber || "");
            } else {
              setSchoolName("School not found");
              setLoadError("School not found in database");
            }
          } catch (error) {
            console.error("Error fetching school details:", error);
            setSchoolName("Error loading school");
            setLoadError(`Error loading school: ${error.message}`);
          }

          setIsLoadingSchool(false);
        } else {
          setSchoolName("Please enter school roll number and press 'Find School'");
          setIsLoadingSchool(false);
        }
      } catch (error) {
        console.error("Error in whoami:", error);
        setSchoolName("Error loading user information");
        setLoadError(`Error loading user: ${error.message}`);
        setIsLoadingSchool(false);
      }
    };

    fetchUserAndSchoolInfo();
  }, [teacherMode, onUserLoaded]);

  useEffect(() => {
    if (isSchoolFound) {
      const loadClasses = async () => {
        setStudentDirectoryError(null);
        setAccessibleStudents([]);
        setHasLoadedStudentDirectory(false);

        try {
          const response = isTeacherStudentFirstFlow
            ? await getClasses()
            : await getClassesBySchoolId(schoolid);
          const transformedClasses = (Array.isArray(response) ? response : [])
            .map(normaliseClass)
            .sort((classA, classB) => {
              if (classA.academicYear !== classB.academicYear) {
                return classB.academicYear?.localeCompare(classA.academicYear || "") || 0;
              }

              return classA.name.localeCompare(classB.name);
            });

          setClasses(transformedClasses);
        } catch (error) {
          console.error("Error fetching classes:", error);
          setLoadError("Classes could not be loaded. Please try again.");
        }
      };

      loadClasses();
    }
  }, [schoolid, isSchoolFound, isTeacherStudentFirstFlow]);

  useEffect(() => {
    if (!isTeacherStudentFirstFlow) {
      return undefined;
    }

    const query = studentSearchTerm.trim();

    if (query.length < STUDENT_SEARCH_MIN_LENGTH) {
      setAccessibleStudents([]);
      setStudentDirectoryError(null);
      setIsLoadingStudentDirectory(false);
      setHasLoadedStudentDirectory(false);
      return undefined;
    }

    let isCurrentSearch = true;
    setAccessibleStudents([]);
    setStudentDirectoryError(null);
    setIsLoadingStudentDirectory(true);
    setHasLoadedStudentDirectory(false);

    const timeoutId = window.setTimeout(() => {
      searchTeacherStudents(query, STUDENT_SEARCH_LIMIT)
        .then((studentsResponse) => {
          if (!isCurrentSearch) {
            return;
          }

          const studentResults = Array.isArray(studentsResponse)
            ? studentsResponse.map(normaliseStudentSearchResult)
            : [];

          setAccessibleStudents(studentResults);
          setHasLoadedStudentDirectory(true);
        })
        .catch((error) => {
          if (!isCurrentSearch) {
            return;
          }

          console.error("Error searching teacher students:", error);
          setStudentDirectoryError(
            "Student search could not be loaded. You can still search by class."
          );
          setAccessibleStudents([]);
          setHasLoadedStudentDirectory(true);
        })
        .finally(() => {
          if (isCurrentSearch) {
            setIsLoadingStudentDirectory(false);
          }
        });
    }, STUDENT_SEARCH_DEBOUNCE_MS);

    return () => {
      isCurrentSearch = false;
      window.clearTimeout(timeoutId);
    };
  }, [isTeacherStudentFirstFlow, studentSearchTerm]);

  useEffect(() => {
    if (selectedClass && !(isTeacherStudentFirstFlow && selectedStudent)) {
      getStudentsByClassId(schoolid, selectedClass)
        .then((response) => {
          setStudents(response);
        })
        .catch((error) => {
          console.error("Error fetching students:", error);
        });
    }
  }, [schoolid, selectedClass, selectedStudent, isTeacherStudentFirstFlow]);

  useEffect(() => {
    if (selectedStudent) {
      const student = students.find((student) => student.id === selectedStudent);
      if (student) {
        setSelectedStudentName(student.name);
      }

      getIndividualStudentResults(selectedStudent)
        .then((response) => {
          setStudentResults(response);
          onProgressStepChange?.(1);
        })
        .catch((error) => {
          console.error("Error fetching student results:", error);
        });
    }
  }, [selectedStudent, students, onProgressStepChange]);

  const normalisedSearchQuery = modalMode ? "" : searchQuery.trim().toLowerCase();
  const normalisedClassSearchTerm = classSearchTerm.trim().toLowerCase();
  const normalisedStudentSearchTerm = studentSearchTerm.trim().toLowerCase();
  const contentWidthClass = modalMode ? "w-100" : "w-75";

  const matchesSearch = useCallback((...values) => {
    if (!normalisedSearchQuery) {
      return true;
    }

    return values.some((value) =>
      String(value || "").toLowerCase().includes(normalisedSearchQuery)
    );
  }, [normalisedSearchQuery]);

  const filteredClasses = useMemo(() => {
    return classes.filter((cls) => {
      const matchesGlobalSearch = matchesSearch(
        cls.displayName,
        cls.name,
        cls.grade,
        cls.academicYear
      );
      const matchesClassSearch =
        !normalisedClassSearchTerm ||
        [cls.displayName, cls.name, cls.grade, cls.academicYear].some((value) =>
          String(value || "").toLowerCase().includes(normalisedClassSearchTerm)
        );
      const matchesGrade =
        classGradeFilter === "all" || cls.gradeBoolean === classGradeFilter;

      return matchesGlobalSearch && matchesClassSearch && matchesGrade;
    });
  }, [classes, classGradeFilter, matchesSearch, normalisedClassSearchTerm]);

  const filteredStudents = useMemo(
    () => students.filter((student) => matchesSearch(student.name)),
    [students, matchesSearch]
  );

  const filteredAccessibleStudents = useMemo(() => {
    if (!isTeacherStudentFirstFlow || !normalisedStudentSearchTerm) {
      return [];
    }

    return accessibleStudents;
  }, [accessibleStudents, isTeacherStudentFirstFlow, normalisedStudentSearchTerm]);

  const filteredStudentResults = useMemo(() => {
    if (!normalisedSearchQuery) {
      return studentResults;
    }

    return studentResults
      .map((assessment) => {
        const assessmentMatches = matchesSearch(assessment.name);
        const tasks = assessmentMatches
          ? assessment.tasks
          : assessment.tasks.filter((task) => matchesSearch(task.name));

        return {
          ...assessment,
          tasks
        };
      })
      .filter((assessment) => matchesSearch(assessment.name) || assessment.tasks.length > 0);
  }, [studentResults, normalisedSearchQuery, matchesSearch]);

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    onSearchQueryChange?.(event.target.value);
  };

  const handleClassSelect = (classId) => {
    if (classId === selectedClass) {
      return;
    }

    setSelectedClass(classId || null);
    setSelectedStudent(null);
    setSelectedStudentName("");
    setStudents([]);
    setStudentResults([]);
    onProgressStepChange?.(0);
  };

  const handleClassChange = (event) => {
    handleClassSelect(event.target.value);
  };

  const handleStudentResultSelect = (studentEntry) => {
    setSelectedClass(studentEntry.classId);
    setStudents([studentEntry.student]);
    setSelectedStudent(studentEntry.id);
    setSelectedStudentName(studentEntry.name);
    setStudentResults([]);
    onProgressStepChange?.(0);
  };

  useEffect(() => {
    if (
      hasAppliedClassPrefill ||
      prefillContext.studentName ||
      !prefillContext.className ||
      selectedClass ||
      classes.length < 1
    ) {
      return;
    }

    const matchingClass = getSingleMatchingClass(classes, prefillContext.className);

    if (matchingClass) {
      setSelectedClass(matchingClass.id);
    }

    setHasAppliedClassPrefill(true);
  }, [
    classes,
    hasAppliedClassPrefill,
    prefillContext.className,
    prefillContext.studentName,
    selectedClass
  ]);

  useEffect(() => {
    if (
      !isTeacherStudentFirstFlow ||
      hasAppliedStudentPrefill ||
      !prefillContext.studentName ||
      isLoadingStudentDirectory ||
      !hasLoadedStudentDirectory
    ) {
      return;
    }

    const matchingStudent = getSingleMatchingStudent(accessibleStudents, prefillContext);

    if (matchingStudent) {
      setSelectedClass(matchingStudent.classId);
      setStudents([matchingStudent.student]);
      setSelectedStudent(matchingStudent.id);
      setSelectedStudentName(matchingStudent.name);
      setStudentResults([]);
      onProgressStepChange?.(0);
    }

    setHasAppliedStudentPrefill(true);
  }, [
    accessibleStudents,
    hasAppliedStudentPrefill,
    hasLoadedStudentDirectory,
    isLoadingStudentDirectory,
    isTeacherStudentFirstFlow,
    onProgressStepChange,
    prefillContext
  ]);

  useEffect(() => {
    if (
      hasAppliedTaskPrefill ||
      !selectedStudent ||
      !prefillContext.taskName ||
      studentResults.length < 1
    ) {
      return;
    }

    const matchingTaskTarget = getPrefilledTaskTarget(studentResults, prefillContext);

    if (matchingTaskTarget) {
      setSelectedTaskInfo({
        task: matchingTaskTarget.task,
        assessmentName: matchingTaskTarget.assessment.name,
        studentName: selectedStudentName,
        answerIds: [matchingTaskTarget.task.answerid]
      });
      setShowDeleteModal(true);
      onProgressStepChange?.(2);
    }

    setHasAppliedTaskPrefill(true);
  }, [
    hasAppliedTaskPrefill,
    onProgressStepChange,
    prefillContext,
    selectedStudent,
    selectedStudentName,
    studentResults
  ]);

  const calculateAverageScore = (tasks) => {
    if (!tasks || tasks.length === 0) return 0;
    const totalScore = tasks.reduce((sum, task) => sum + task.score, 0);
    return (totalScore / tasks.length).toFixed(2);
  };

  const handleShowDeleteModal = (task, assessmentName) => {
    setSelectedTaskInfo({
      task,
      assessmentName,
      studentName: selectedStudentName,
      answerIds: [task.answerid]
    });
    setShowDeleteModal(true);
    onProgressStepChange?.(2);
  };

  const handleShowDeleteModalForAssessment = (assessment) => {
    const allAnswerIds = assessment.tasks.map((task) => task.answerid);
    setSelectedTaskInfo({
      task: null,
      assessmentName: assessment.name,
      studentName: selectedStudentName,
      answerIds: allAnswerIds
    });
    setShowDeleteModal(true);
    onProgressStepChange?.(2);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedTaskInfo(null);
    onProgressStepChange?.(studentResults.length > 0 ? 1 : 0);
  };

  const handleDeleteConfirmed = (taskName, deletionReason, shouldCloseModal = true) => {
    if (selectedTaskInfo) {
      console.log(`Task(s) ${taskName} deleted with reason: ${deletionReason}`);

      const updatedResults = studentResults.map((assessment) => {
        if (selectedTaskInfo.task) {
          return {
            ...assessment,
            tasks: assessment.tasks.filter(
              (task) => task.answerid !== selectedTaskInfo.task.answerid
            )
          };
        }

        if (assessment.name === selectedTaskInfo.assessmentName) {
          return {
            ...assessment,
            tasks: []
          };
        }

        return assessment;
      });

      setStudentResults(updatedResults);

      if (shouldCloseModal) {
        setShowDeleteModal(false);
        setSelectedTaskInfo(null);
      }
    }
  };

  const getScoreColor = (score) => {
    if (score >= 0.8) return "success";
    if (score >= 0.6) return "warning";
    return "danger";
  };

  const classFallbackControls = (
    <>
      <Row className="align-items-end">
        <Col md={7}>
          <Form.Group className="mb-3" controlId="resetTaskClassSearch">
            <Form.Label>
              <strong>Search Class</strong>
            </Form.Label>
            <Form.Control
              type="search"
              value={classSearchTerm}
              onChange={(event) => setClassSearchTerm(event.target.value)}
              placeholder="Search by class, grade or academic year"
            />
          </Form.Group>
        </Col>
        <Col md={5}>
          <Form.Group className="mb-3" controlId="resetTaskClassGradeFilter">
            <Form.Label>
              <strong>Grade Level</strong>
            </Form.Label>
            <Form.Control
              as="select"
              value={classGradeFilter}
              onChange={(event) => setClassGradeFilter(event.target.value)}
            >
              {GRADE_FILTER_OPTIONS.map((grade) => (
                <option key={grade.value} value={grade.value}>
                  {grade.label}
                </option>
              ))}
            </Form.Control>
          </Form.Group>
        </Col>
      </Row>
      <div className="reset-class-results mb-3">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <strong>Matching Classes</strong>
          {classes.length > 0 && (
            <small className="text-muted">
              {filteredClasses.length} of {classes.length}
            </small>
          )}
        </div>
        {filteredClasses.length > 0 ? (
          <div className="reset-class-result-list" aria-label="Matching classes">
            {filteredClasses.map((cls) => {
              const classDisplayName = cls.displayName || formatClassDisplayName(cls);
              const classTitle = `${cls.academicYear ? `[${cls.academicYear}] ` : ""}${cls.name}`;
              const isSelectedClass = selectedClass === cls.id;

              return (
                <button
                  type="button"
                  key={cls.id}
                  className={`reset-class-result${isSelectedClass ? " selected" : ""}`}
                  onClick={() => handleClassSelect(cls.id)}
                  aria-label={`Select ${classDisplayName}`}
                  aria-pressed={isSelectedClass}
                >
                  <span className="reset-class-result-copy">
                    <span className="reset-class-result-title">{classTitle}</span>
                    {cls.grade && (
                      <span className="reset-class-result-meta">{cls.grade}</span>
                    )}
                  </span>
                  {isSelectedClass && (
                    <span className="reset-class-result-status">Selected</span>
                  )}
                </button>
              );
            })}
          </div>
        ) : (
          <p className="text-secondary mb-0">No classes match the current filters.</p>
        )}
      </div>
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3" controlId="resetTaskClassSelect">
            <Form.Label>
              <strong>Select Class</strong>
            </Form.Label>
            <Form.Control as="select" value={selectedClass || ""} onChange={handleClassChange}>
              <option value="">Select Class</option>
              {filteredClasses.map((cls) => (
                <option key={cls.id} value={cls.id}>
                  {cls.displayName || formatClassDisplayName(cls)}
                </option>
              ))}
            </Form.Control>
            {classes.length > 0 && (
              <Form.Text className="text-muted">
                Showing {filteredClasses.length} of {classes.length} classes
              </Form.Text>
            )}
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3" controlId="resetTaskStudentSelect">
            <Form.Label>
              <strong>Select Student</strong>
            </Form.Label>
            <Form.Control
              as="select"
              value={selectedStudent || ""}
              onChange={(event) => setSelectedStudent(event.target.value)}
              disabled={!selectedClass}
            >
              <option value="">Select Student</option>
              {filteredStudents.map((student) => (
                <option key={student.id} value={student.id}>
                  {student.name}
                </option>
              ))}
            </Form.Control>
          </Form.Group>
        </Col>
      </Row>
    </>
  );

  return (
    <>
      {teacherMode && !modalMode && (
        <div className={`${contentWidthClass} mb-3`}>
          <Form.Group controlId={modalMode ? "modalResetTaskSearch" : "resetTaskSearch"}>
            <Form.Label className={modalMode ? "" : "text-white"}>
              <strong>Search reset targets</strong>
            </Form.Label>
            <Form.Control
              type="search"
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder="Search classes, students, assessments or tasks"
            />
          </Form.Group>
        </div>
      )}

      {teacherMode && !modalMode && (
        <div className={`${contentWidthClass} mb-4`}>
          <Alert variant="info">
            <strong>School Administrator Mode</strong>
            <p className="mb-0">You can reset tasks for students in your school only.</p>
          </Alert>
        </div>
      )}

      {loadError && (
        <div className={`${contentWidthClass} mb-4`}>
          <Alert variant="danger">
            <strong>Error Loading School</strong>
            <p className="mb-0">{loadError}</p>
          </Alert>
        </div>
      )}

      {!(teacherMode && modalMode) && (
        <div className={`${contentWidthClass} mt-2 bg-white rounded shadow-sm p-4 mb-4`}>
          {teacherMode ? (
            <>
              <h4 className="mb-3">Your School</h4>
              <Alert variant="secondary" className="mb-3">
                <FaChalkboardTeacher className="me-2" />
                You are resetting tasks for students in the following school.
              </Alert>
              {isLoadingSchool ? (
                <div className="text-center py-4">
                  <Spinner animation="border" variant="primary" />
                  <p className="mt-2">Loading your school information...</p>
                </div>
              ) : (
                <>
                  <h3 className="text-primary">{schoolName}</h3>
                  {rollNumber && <p className="text-muted">Roll Number: {rollNumber}</p>}
                </>
              )}
            </>
          ) : (
            <>
              <h4 className="mb-3">School Selection</h4>
              <SchoolRollNumberInput
                onSchoolIdRetrieved={setSchoolid}
                schoolNameRetrieved={(name, rollNum) => {
                  setSchoolName(name);
                  if (rollNum) setRollNumber(rollNum);
                }}
              />

              {schoolName &&
                schoolName !== "Please enter school roll number and press 'Find School'" && (
                  <div className="mt-2 p-2 bg-light rounded">
                    <strong>Selected School:</strong> {schoolName}
                  </div>
                )}
            </>
          )}
        </div>
      )}

      {teacherMode && modalMode && isLoadingSchool && (
        <div className={`${contentWidthClass} bg-white rounded shadow-sm p-4 mb-4 text-center`}>
          <Spinner animation="border" variant="primary" />
          <p className="mt-2 mb-0">Loading reset options...</p>
        </div>
      )}

      {isSchoolFound && (
        <div className={`${contentWidthClass} bg-white rounded shadow-sm p-4 mb-4`}>
          <h4 className="mb-3">{isTeacherStudentFirstFlow ? "Find Student" : "Student Selection"}</h4>
          {isTeacherStudentFirstFlow && (
            <div className="reset-student-search mb-4">
              <Form.Group className="mb-3" controlId="resetTaskStudentSearch">
                <Form.Label>
                  <strong>Search Student</strong>
                </Form.Label>
                <Form.Control
                  type="search"
                  value={studentSearchTerm}
                  onChange={(event) => setStudentSearchTerm(event.target.value)}
                  placeholder="Start typing a student name"
                />
                <Form.Text className="text-muted">
                  Search students in your available classes.
                </Form.Text>
              </Form.Group>

              {studentDirectoryError && (
                <Alert variant="warning" className="py-2 mb-3">
                  {studentDirectoryError}
                </Alert>
              )}

              <div className="reset-student-results">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <strong>Matching Students</strong>
                  {normalisedStudentSearchTerm && accessibleStudents.length > 0 && (
                    <small className="text-muted">
                      {filteredAccessibleStudents.length} of {accessibleStudents.length}
                    </small>
                  )}
                </div>

                {isLoadingStudentDirectory ? (
                  <div className="reset-student-search-state">
                    <Spinner animation="border" size="sm" variant="primary" />
                    <span>Loading student search...</span>
                  </div>
                ) : normalisedStudentSearchTerm.length < STUDENT_SEARCH_MIN_LENGTH ? (
                  <p className="text-secondary mb-0">
                    Enter at least 2 characters to search your available classes.
                  </p>
                ) : filteredAccessibleStudents.length > 0 ? (
                  <div className="reset-student-result-list" aria-label="Matching students">
                    {filteredAccessibleStudents.map((studentEntry) => {
                      const isSelectedStudent =
                        selectedStudent === studentEntry.id &&
                        selectedClass === studentEntry.classId;

                      return (
                        <button
                          type="button"
                          key={`${studentEntry.classId}-${studentEntry.id}`}
                          className={`reset-student-result${isSelectedStudent ? " selected" : ""}`}
                          onClick={() => handleStudentResultSelect(studentEntry)}
                          aria-label={`Select ${studentEntry.name} from ${studentEntry.classDisplayName}`}
                          aria-pressed={isSelectedStudent}
                        >
                          <span className="reset-student-result-copy">
                            <span className="reset-student-result-title">
                              {studentEntry.name}
                            </span>
                            <span className="reset-student-result-meta">
                              {studentEntry.classDisplayName}
                            </span>
                          </span>
                          {isSelectedStudent && (
                            <span className="reset-student-result-status">Selected</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                ) : hasLoadedStudentDirectory && !studentDirectoryError ? (
                  <p className="text-secondary mb-0">
                    No matching students found in your available classes.
                  </p>
                ) : null}
              </div>
            </div>
          )}

          {isTeacherStudentFirstFlow ? (
            <div className="reset-class-fallback">
              <button
                type="button"
                className="reset-class-fallback-toggle"
                aria-expanded={isClassFallbackOpen}
                aria-controls="resetClassFallbackContent"
                onClick={() => setIsClassFallbackOpen((isOpen) => !isOpen)}
              >
                Search by class
              </button>
              {isClassFallbackOpen && (
                <div id="resetClassFallbackContent" className="reset-class-fallback-content">
                  {classFallbackControls}
                </div>
              )}
            </div>
          ) : (
            classFallbackControls
          )}
        </div>
      )}

      {studentResults.length > 0 ? (
        <div className={`${contentWidthClass} mb-4`}>
          <div className="bg-white rounded shadow-sm p-4 mb-3">
            <h4 className="border-bottom pb-2">
              Assessment Results for {selectedStudentName}
            </h4>
            {normalisedSearchQuery && filteredStudentResults.length === 0 ? (
              <p className="text-secondary mt-3 mb-0">
                No assessments or tasks match your search.
              </p>
            ) : (
              <Accordion defaultActiveKey="0" className="mt-3">
                {filteredStudentResults.map((assessment, idx) => (
                  <Accordion.Item
                    eventKey={idx.toString()}
                    key={assessment.id}
                    className="mb-3 border rounded overflow-hidden"
                  >
                    <Accordion.Header>
                      <div className="d-flex justify-content-between align-items-center w-100">
                        <div>
                          <strong className="me-2">{assessment.name}</strong>
                        </div>
                        <div className="d-flex align-items-center">
                          <div className="d-flex align-items-center me-3">
                            <span className="me-1">Time</span>
                            <span>
                              {secondsToTime(
                                assessment.tasks.reduce(
                                  (total, task) => total + task.duration,
                                  0
                                )
                              )}
                            </span>
                          </div>
                          <div className="d-flex align-items-center me-3">
                            <span className="me-1">Score</span>
                            <span
                              className={`text-${getScoreColor(
                                calculateAverageScore(assessment.tasks)
                              )}`}
                            >
                              {(calculateAverageScore(assessment.tasks) * 100).toFixed(0)}%
                            </span>
                          </div>
                        </div>
                      </div>
                    </Accordion.Header>
                    <Accordion.Body className="bg-light">
                      {assessment.tasks.length > 0 && (
                        <div className="d-flex justify-content-end mb-3">
                          <Button
                            variant="outline-danger"
                            size="sm"
                            className="d-flex align-items-center"
                            onClick={() => handleShowDeleteModalForAssessment(assessment)}
                          >
                            <span className="me-1">Reset All</span>
                          </Button>
                        </div>
                      )}
                      {assessment.tasks.length > 0 ? (
                        assessment.tasks.map((task) => (
                          <Card key={task.answerid} className="mb-3 border-0 shadow-sm">
                            <Card.Body>
                              <div className="d-flex justify-content-between align-items-center">
                                <div className="d-flex align-items-center">
                                  <div>
                                    <h5 className="mb-0">{task.name}</h5>
                                    <div className="mt-2 d-flex">
                                      <div className="me-3 d-flex align-items-center">
                                        <small className="text-secondary">
                                          {secondsToTime(task.duration)}
                                        </small>
                                      </div>
                                      <div className="d-flex align-items-center">
                                        <small className={`text-${getScoreColor(task.score)}`}>
                                          {(task.score * 100).toFixed(0)}%
                                        </small>
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                <Button
                                  variant="outline-warning"
                                  size="sm"
                                  className="d-flex align-items-center"
                                  onClick={() => handleShowDeleteModal(task, assessment.name)}
                                >
                                  <span className="me-1">Retake</span>
                                </Button>
                              </div>
                            </Card.Body>
                          </Card>
                        ))
                      ) : (
                        <div className="text-center p-3">
                          <p className="text-muted mb-0">
                            All tasks in this assessment have been reset
                          </p>
                        </div>
                      )}
                    </Accordion.Body>
                  </Accordion.Item>
                ))}
              </Accordion>
            )}
          </div>
        </div>
      ) : selectedStudent ? (
        <div className={`${contentWidthClass} bg-white rounded shadow-sm p-4 text-center`}>
          <p className="text-secondary mt-3">No assessment results found for this student.</p>
        </div>
      ) : isSchoolFound && !isLoadingSchool ? (
        <div className={`${contentWidthClass} bg-white rounded shadow-sm p-4 text-center`}>
          <p className="text-secondary">
            {isTeacherStudentFirstFlow
              ? "Search for a student to view their assessment results."
              : "Please select a class and student to view their assessment results."}
          </p>
        </div>
      ) : !isSchoolFound && !isLoadingSchool ? (
        <div className={`${contentWidthClass} text-center py-5`}>
          <h4 className={modalMode ? "text-secondary" : "text-white"}>
            {teacherMode
              ? loadError || "Unable to load school information. Please contact support."
              : "Please select a school to begin."}
          </h4>
        </div>
      ) : null}

      {selectedTaskInfo && (
        <DeleteAnswerModal
          show={showDeleteModal}
          handleClose={handleCloseDeleteModal}
          taskName={selectedTaskInfo.task ? selectedTaskInfo.task.name : "All Tasks"}
          studentName={selectedTaskInfo.studentName}
          assessmentName={selectedTaskInfo.assessmentName}
          handleDelete={handleDeleteConfirmed}
          answerIds={selectedTaskInfo.answerIds}
          teacherid={teacherid}
          isRetakeFullAssessment={!selectedTaskInfo.task}
          isClassLevel={false}
          progressSteps={modalMode ? RESET_TASK_PROGRESS_STEPS : null}
          onProgressStepChange={onProgressStepChange}
          openingContext="Choose the retake reason carefully. This reset will remove the selected answer data so the student can complete the task again."
          successContext="The reset reason has been saved with this change."
          prefillReason={prefillContext.reason}
          onWorkflowComplete={onWorkflowComplete}
        />
      )}
    </>
  );
}

export default ResetTasksFlow;
